import _ from 'lodash';

import { PendingItemStatus } from '@appTypes/enums';
import { AwardId } from '@appTypes/enums/awards';
import { IS_QA_ENV } from '@config/config';
import { Program, ProgramId } from '@shared/constants';

export const moduleName = 'advancement';
export const mobileWizardId = 'ROSTER_MOBILE';
export const desktopWizardId = 'ROSTER_DESKTOP';
export { reportTypes } from '../reports/constants';

export const quickEntryTabNames = Object.freeze({
  YOUTHS: 'youths',
  LEADERS: 'leaders',
  PARENTS: 'parents',
});

export const versionStatusLabels = Object.freeze({
  UPCOMING: 'versionStatus.upcoming',
  CURRENT: 'versionStatus.current',
  EXPIRED: 'versionStatus.expired',
});

export const pocketCertificateTypes = {
  ADVENTURE: 'adventures',
  LION: 'lion',
  BOBCAT: 'bobcat',
  TIGER: 'tiger',
  WOLF: 'wolf',
  BEAR: 'bear',
  WEBELOS: 'webelos',
  ARROW: 'arrow of light',
  MERIT_BADGE: 'meritBadges',
} as const;

export type PocketCertificateType =
  (typeof pocketCertificateTypes)[keyof typeof pocketCertificateTypes];

export const pocketCertificateTypePlural: Record<
  PocketCertificateType,
  string
> = {
  [pocketCertificateTypes.ADVENTURE]: 'adventures',
  [pocketCertificateTypes.LION]: 'lions',
  [pocketCertificateTypes.BOBCAT]: 'bobcats',
  [pocketCertificateTypes.TIGER]: 'tigers',
  [pocketCertificateTypes.WOLF]: 'wolves',
  [pocketCertificateTypes.BEAR]: 'bears',
  [pocketCertificateTypes.WEBELOS]: 'webelos',
  [pocketCertificateTypes.ARROW]: 'aol',
  [pocketCertificateTypes.MERIT_BADGE]: 'meritBadges',
};

export const pocketCertificateTypesList: PocketCertificateType[] = [
  pocketCertificateTypes.ADVENTURE,
  pocketCertificateTypes.LION,
  pocketCertificateTypes.TIGER,
  pocketCertificateTypes.WOLF,
  pocketCertificateTypes.BEAR,
  pocketCertificateTypes.WEBELOS,
  pocketCertificateTypes.ARROW,
  pocketCertificateTypes.MERIT_BADGE,
];

export const notApprovableRankIds = [7, 22, 18];

// List of Requirement Number that has linked adventures
// This is to easily determine which Rank to recalculate upon approving
// This will be utilize until EAGRE-8260 is available
export const linkedRequirementIds = [
  2262, 2263, 2252, 2253, 2243, 2242, 2233, 2232, 2223, 2222, 2273, 2272, 597,
  605, 1510,
];
export const linkedAdventuresAOL = [
  151, 64, 162, 153, 157, 155, 149, 150, 152, 154, 75, 156, 158, 159, 78, 79,
  160, 161, 163, 164, 165, 166,
];
export const linkedAdventuresWebelos = [
  132, 62, 61, 139, 141, 140, 68, 130, 69, 70, 131, 71, 133, 134, 135, 74, 136,
  137, 138, 142, 143, 144, 145, 146, 147, 148,
];
export const linkedAdventuresBear = [
  119, 115, 116, 124, 127, 122, 46, 112, 114, 113, 45, 117, 118, 120, 121, 49,
  50, 123, 52, 125, 53, 55, 126, 128, 56, 129,
];
export const linkedAdventuresWolf = [
  20, 21, 22, 23, 24, 25, 26, 27, 28, 30, 31, 32, 33, 37, 105, 38, 98,
];
export const linkedAdventuresTiger = [
  2, 1, 4, 5, 3, 6, 7, 10, 11, 104, 12, 16, 13, 14, 17, 15, 104,
];
export const linkedAdventuresLion = [85, 87, 86, 89, 88];

export const PALM_AWARDS = new Set([
  AwardId.EaglePalmPin10Bronze,
  AwardId.EaglePalmPin10Bronze_2,
  AwardId.EaglePalmPin10Bronze_3,
  AwardId.EaglePalmPin10Bronze_4,
  AwardId.EaglePalmPin11Gold,
  AwardId.EaglePalmPin11Gold_2,
  AwardId.EaglePalmPin11Gold_3,
  AwardId.EaglePalmPin11Gold_4,
  AwardId.EaglePalmPin12Silver,
  AwardId.EaglePalmPin12Silver_2,
  AwardId.EaglePalmPin12Silver_3,
  AwardId.EaglePalmPin12Silver_4,
  AwardId.EaglePalmPin13Bronze,
  AwardId.EaglePalmPin13Bronze_2,
  AwardId.EaglePalmPin13Bronze_3,
  AwardId.EaglePalmPin13Bronze_4,
  AwardId.EaglePalmPin14Gold,
  AwardId.EaglePalmPin14Gold_2,
  AwardId.EaglePalmPin14Gold_3,
  AwardId.EaglePalmPin14Gold_4,
  AwardId.EaglePalmPin15Silver,
  AwardId.EaglePalmPin15Silver_2,
  AwardId.EaglePalmPin15Silver_3,
  AwardId.EaglePalmPin15Silver_4,
  AwardId.EaglePalmPin16Bronze,
  AwardId.EaglePalmPin16Bronze_2,
  AwardId.EaglePalmPin16Bronze_3,
  AwardId.EaglePalmPin16Bronze_4,
  AwardId.EaglePalmPin17Gold,
  AwardId.EaglePalmPin17Gold_2,
  AwardId.EaglePalmPin17Gold_3,
  AwardId.EaglePalmPin17Gold_4,
  AwardId.EaglePalmPin18Silver,
  AwardId.EaglePalmPin18Silver_2,
  AwardId.EaglePalmPin18Silver_3,
  AwardId.EaglePalmPin18Silver_4,
  AwardId.EaglePalmPin19Bronze,
  AwardId.EaglePalmPin19Bronze_2,
  AwardId.EaglePalmPin19Bronze_3,
  AwardId.EaglePalmPin19Bronze_4,
  AwardId.EaglePalmPin1Bronze,
  AwardId.EaglePalmPin1Bronze_2,
  AwardId.EaglePalmPin1Bronze_3,
  AwardId.EaglePalmPin1Bronze_4,
  AwardId.EaglePalmPin20Gold,
  AwardId.EaglePalmPin20Gold_2,
  AwardId.EaglePalmPin20Gold_3,
  AwardId.EaglePalmPin20Gold_4,
  AwardId.EaglePalmPin21Silver,
  AwardId.EaglePalmPin21Silver_2,
  AwardId.EaglePalmPin21Silver_3,
  AwardId.EaglePalmPin21Silver_4,
  AwardId.EaglePalmPin22Bronze,
  AwardId.EaglePalmPin22Bronze_2,
  AwardId.EaglePalmPin22Bronze_3,
  AwardId.EaglePalmPin22Bronze_4,
  AwardId.EaglePalmPin23Gold,
  AwardId.EaglePalmPin23Gold_2,
  AwardId.EaglePalmPin23Gold_3,
  AwardId.EaglePalmPin23Gold_4,
  AwardId.EaglePalmPin24Silver,
  AwardId.EaglePalmPin2Gold,
  AwardId.EaglePalmPin2Gold_2,
  AwardId.EaglePalmPin2Gold_3,
  AwardId.EaglePalmPin2Gold_4,
  AwardId.EaglePalmPin3Silver,
  AwardId.EaglePalmPin3Silver_2,
  AwardId.EaglePalmPin3Silver_3,
  AwardId.EaglePalmPin3Silver_4,
  AwardId.EaglePalmPin4Bronze,
  AwardId.EaglePalmPin4Bronze_2,
  AwardId.EaglePalmPin4Bronze_3,
  AwardId.EaglePalmPin4Bronze_4,
  AwardId.EaglePalmPin5Gold,
  AwardId.EaglePalmPin5Gold_2,
  AwardId.EaglePalmPin5Gold_3,
  AwardId.EaglePalmPin5Gold_4,
  AwardId.EaglePalmPin6Silver,
  AwardId.EaglePalmPin6Silver_2,
  AwardId.EaglePalmPin6Silver_3,
  AwardId.EaglePalmPin6Silver_4,
  AwardId.EaglePalmPin7Bronze,
  AwardId.EaglePalmPin7Bronze_2,
  AwardId.EaglePalmPin7Bronze_3,
  AwardId.EaglePalmPin7Bronze_4,
  AwardId.EaglePalmPin8Gold,
  AwardId.EaglePalmPin8Gold_2,
  AwardId.EaglePalmPin8Gold_3,
  AwardId.EaglePalmPin8Gold_4,
  AwardId.EaglePalmPin9Silver,
  AwardId.EaglePalmPin9Silver_2,
  AwardId.EaglePalmPin9Silver_3,
  AwardId.EaglePalmPin9Silver_4,
]);

export const NOA_AWARDS = new Set([
  AwardId.NoaAwardAdventure,
  AwardId.NoaAwardAdventureDevice1Gold,
  AwardId.NoaAwardAdventureDevice2Gold,
  AwardId.NoaAwardAdventureDevice3Gold,
  AwardId.NoaAwardAdventureDevice4Silver,
  AwardId.NoaAwardAdventureDevice5Gold,
  AwardId.NoaAwardAdventureDevice6Gold,
  AwardId.NoaAwardAdventureDevice7Gold,
  AwardId.NoaAwardAdventureDevice8Silver,
  AwardId.NoaAwardAdventureDevice9Gold,
  AwardId.NoaAwardAdventureDevice10Gold,
  AwardId.NoaAwardAdventureDevice11Gold,
  AwardId.NoaAwardAdventureDevice12Silver,
  AwardId.NoaAwardAdventureDevice13Gold,
  AwardId.NoaAwardAdventureDevice14Gold,
  AwardId.NoaAwardAdventureDevice15Gold,
  AwardId.NoaAwardAdventureDevice16Silver,
  AwardId.NoaAwardAdventureDevice17Gold,
  AwardId.NoaAwardAdventureDevice18Gold,
  AwardId.NoaAwardAdventureDevice19Gold,
  AwardId.NoaAwardAdventureDevice20Silver,
  AwardId.NoaAwardAdventureDevice21Gold,
  AwardId.NoaAwardAdventureDevice22Gold,
  AwardId.NoaAwardAdventureDevice23Gold,
  AwardId.NoaAwardAdventureDevice24Silver,
  AwardId.NoaAwardAquatics,
  AwardId.NoaAwardAquaticsDevice1Gold,
  AwardId.NoaAwardAquaticsDevice2Gold,
  AwardId.NoaAwardAquaticsDevice3Gold,
  AwardId.NoaAwardAquaticsDevice4Silver,
  AwardId.NoaAwardAquaticsDevice5Gold,
  AwardId.NoaAwardAquaticsDevice6Gold,
  AwardId.NoaAwardAquaticsDevice7Gold,
  AwardId.NoaAwardAquaticsDevice8Silver,
  AwardId.NoaAwardAquaticsDevice9Gold,
  AwardId.NoaAwardAquaticsDevice10Gold,
  AwardId.NoaAwardAquaticsDevice11Gold,
  AwardId.NoaAwardAquaticsDevice12Silver,
  AwardId.NoaAwardAquaticsDevice13Gold,
  AwardId.NoaAwardAquaticsDevice14Gold,
  AwardId.NoaAwardAquaticsDevice15Gold,
  AwardId.NoaAwardAquaticsDevice16Silver,
  AwardId.NoaAwardAquaticsDevice17Gold,
  AwardId.NoaAwardAquaticsDevice18Gold,
  AwardId.NoaAwardAquaticsDevice19Gold,
  AwardId.NoaAwardAquaticsDevice20Silver,
  AwardId.NoaAwardAquaticsDevice21Gold,
  AwardId.NoaAwardAquaticsDevice22Gold,
  AwardId.NoaAwardAquaticsDevice23Gold,
  AwardId.NoaAwardAquaticsDevice24Silver,
  AwardId.NoaAwardAquaticsDevice25Gold,
  AwardId.NoaAwardAquaticsDevice26Gold,
  AwardId.NoaAwardAquaticsDevice27Gold,
  AwardId.NoaAwardAquaticsDevice28Silver,
  AwardId.NoaAwardAquaticsDevice29Gold,
  AwardId.NoaAwardAquaticsDevice30Gold,
  AwardId.NoaAwardAquaticsDevice31Gold,
  AwardId.NoaAwardAquaticsDevice32Silver,
  AwardId.NoaAwardCamping,
  AwardId.NoaAwardCampingDevice1Gold,
  AwardId.NoaAwardCampingDevice2Gold,
  AwardId.NoaAwardCampingDevice3Gold,
  AwardId.NoaAwardCampingDevice4Silver,
  AwardId.NoaAwardCampingDevice5Gold,
  AwardId.NoaAwardCampingDevice6Gold,
  AwardId.NoaAwardCampingDevice7Gold,
  AwardId.NoaAwardCampingDevice8Silver,
  AwardId.NoaAwardCampingDevice9Gold,
  AwardId.NoaAwardCampingDevice10Gold,
  AwardId.NoaAwardCampingDevice11Gold,
  AwardId.NoaAwardCampingDevice12Silver,
  AwardId.NoaAwardCampingDevice13Gold,
  AwardId.NoaAwardCampingDevice14Gold,
  AwardId.NoaAwardCampingDevice15Gold,
  AwardId.NoaAwardCampingDevice16Silver,
  AwardId.NoaAwardCampingDevice17Gold,
  AwardId.NoaAwardCampingDevice18Gold,
  AwardId.NoaAwardCampingDevice19Gold,
  AwardId.NoaAwardCampingDevice20Silver,
  AwardId.NoaAwardCampingDevice21Gold,
  AwardId.NoaAwardCampingDevice22Gold,
  AwardId.NoaAwardCampingDevice23Gold,
  AwardId.NoaAwardCampingDevice24Silver,
  AwardId.NoaAwardCenterEmblem,
  AwardId.NoaAwardConservation,
  AwardId.NoaAwardConservationDevice1Gold,
  AwardId.NoaAwardConservationDevice2Gold,
  AwardId.NoaAwardConservationDevice3Gold,
  AwardId.NoaAwardConservationDevice4Silver,
  AwardId.NoaAwardConservationDevice5Gold,
  AwardId.NoaAwardConservationDevice6Gold,
  AwardId.NoaAwardConservationDevice7Gold,
  AwardId.NoaAwardConservationDevice8Silver,
  AwardId.NoaAwardConservationDevice9Gold,
  AwardId.NoaAwardConservationDevice10Gold,
  AwardId.NoaAwardConservationDevice11Gold,
  AwardId.NoaAwardConservationDevice12Silver,
  AwardId.NoaAwardConservationDevice13Gold,
  AwardId.NoaAwardConservationDevice14Gold,
  AwardId.NoaAwardConservationDevice15Gold,
  AwardId.NoaAwardConservationDevice16Silver,
  AwardId.NoaAwardConservationDevice17Gold,
  AwardId.NoaAwardConservationDevice18Gold,
  AwardId.NoaAwardConservationDevice19Gold,
  AwardId.NoaAwardConservationDevice20Silver,
  AwardId.NoaAwardConservationDevice21Gold,
  AwardId.NoaAwardConservationDevice22Gold,
  AwardId.NoaAwardConservationDevice23Gold,
  AwardId.NoaAwardConservationDevice24Silver,
  AwardId.NoaAwardHiking,
  AwardId.NoaAwardHikingDevice1Gold,
  AwardId.NoaAwardHikingDevice2Gold,
  AwardId.NoaAwardHikingDevice3Gold,
  AwardId.NoaAwardHikingDevice4Silver,
  AwardId.NoaAwardHikingDevice5Gold,
  AwardId.NoaAwardHikingDevice6Gold,
  AwardId.NoaAwardHikingDevice7Gold,
  AwardId.NoaAwardHikingDevice8Silver,
  AwardId.NoaAwardHikingDevice9Gold,
  AwardId.NoaAwardHikingDevice10Gold,
  AwardId.NoaAwardHikingDevice11Gold,
  AwardId.NoaAwardHikingDevice12Silver,
  AwardId.NoaAwardHikingDevice13Gold,
  AwardId.NoaAwardHikingDevice14Gold,
  AwardId.NoaAwardHikingDevice15Gold,
  AwardId.NoaAwardHikingDevice16Silver,
  AwardId.NoaAwardHikingDevice17Gold,
  AwardId.NoaAwardHikingDevice18Gold,
  AwardId.NoaAwardHikingDevice19Gold,
  AwardId.NoaAwardHikingDevice20Silver,
  AwardId.NoaAwardHikingDevice21Gold,
  AwardId.NoaAwardHikingDevice22Gold,
  AwardId.NoaAwardHikingDevice23Gold,
  AwardId.NoaAwardHikingDevice24Silver,
  AwardId.NoaAwardHikingDevice25Gold,
  AwardId.NoaAwardHikingDevice26Gold,
  AwardId.NoaAwardHikingDevice27Gold,
  AwardId.NoaAwardHikingDevice28Silver,
  AwardId.NoaAwardHikingDevice29Gold,
  AwardId.NoaAwardHikingDevice30Gold,
  AwardId.NoaAwardHikingDevice31Gold,
  AwardId.NoaAwardHikingDevice32Silver,
  AwardId.NoaAwardHikingDevice33Gold,
  AwardId.NoaAwardHikingDevice34Gold,
  AwardId.NoaAwardHikingDevice35Gold,
  AwardId.NoaAwardHikingDevice36Silver,
  AwardId.NoaAwardHikingDevice37Gold,
  AwardId.NoaAwardHikingDevice38Gold,
  AwardId.NoaAwardHikingDevice39Gold,
  AwardId.NoaAwardHikingDevice40Silver,
  AwardId.NoaAwardRiding,
  AwardId.NoaAwardRidingDevice1Gold,
  AwardId.NoaAwardRidingDevice2Gold,
  AwardId.NoaAwardRidingDevice3Gold,
  AwardId.NoaAwardRidingDevice4Silver,
  AwardId.NoaAwardRidingDevice5Gold,
  AwardId.NoaAwardRidingDevice6Gold,
  AwardId.NoaAwardRidingDevice7Gold,
  AwardId.NoaAwardRidingDevice8Silver,
  AwardId.NoaAwardRidingDevice9Gold,
  AwardId.NoaAwardRidingDevice10Gold,
  AwardId.NoaAwardRidingDevice11Gold,
  AwardId.NoaAwardRidingDevice12Silver,
  AwardId.NoaAwardRidingDevice13Gold,
  AwardId.NoaAwardRidingDevice14Gold,
  AwardId.NoaAwardRidingDevice15Gold,
  AwardId.NoaAwardRidingDevice16Silver,
  AwardId.NoaAwardRidingDevice17Gold,
  AwardId.NoaAwardRidingDevice18Gold,
  AwardId.NoaAwardRidingDevice19Gold,
  AwardId.NoaAwardRidingDevice20Silver,
  AwardId.NoaAwardRidingDevice21Gold,
  AwardId.NoaAwardRidingDevice22Gold,
  AwardId.NoaAwardRidingDevice23Gold,
  AwardId.NoaAwardRidingDevice24Silver,
  AwardId.NoaAwardRidingDevice25Gold,
  AwardId.NoaAwardRidingDevice26Gold,
  AwardId.NoaAwardRidingDevice27Gold,
  AwardId.NoaAwardRidingDevice28Silver,
]);

export const CUB_SCOUT_AWARDS = new Set([
  AwardId.CommunityOrganizationAward,
  AwardId.ConservationGoodTurnAwardCS,
  AwardId.FiremnChitAOL,
  AwardId.FoundersBarYouth,
  AwardId.HeroismAward,
  AwardId.HonorMedal,
  AwardId.HonorMedalWithCrossedPalms,
  AwardId.JamesEWestFellowshipAward,
  AwardId.JamesMStewartAward,
  AwardId.LocalCouncilCertificateOfMerit,
  AwardId.MedalOfMerit,
  AwardId.MessengerOfPeaceAmbassador,
  AwardId.MessengerOfPeaceChampion,
  AwardId.MessengersOfPeace,
  AwardId.MessengersOfPeaceDistinguishedServiceProject,
  AwardId.MessengersOfPeace_2,
  AwardId.NatlCouncilCertOfMeritYouth,
  AwardId.PolarBearAward,
  AwardId.RecruiterStrip,
  AwardId.ReligiousEmblemYouth,
  AwardId.InternationalSpiritAwardYouth,
  AwardId.InterpreterStrip,
]);

export const SCOUTS_BSA_AWARDS = new Set([
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNST,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNational,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearCouncil,
  AwardId.BoardsailingAward,
  AwardId.CommunityOrganizationAward,
  AwardId.CompleteAngler,
  AwardId.ConservationGoodTurnAwardSct,
  AwardId.DenChiefServiceAward,
  AwardId.EaglePalmPin10Bronze,
  AwardId.EaglePalmPin10Bronze_2,
  AwardId.EaglePalmPin10Bronze_3,
  AwardId.EaglePalmPin11Gold,
  AwardId.EaglePalmPin11Gold_2,
  AwardId.EaglePalmPin11Gold_3,
  AwardId.EaglePalmPin12Silver,
  AwardId.EaglePalmPin12Silver_2,
  AwardId.EaglePalmPin12Silver_3,
  AwardId.EaglePalmPin13Bronze,
  AwardId.EaglePalmPin13Bronze_2,
  AwardId.EaglePalmPin13Bronze_3,
  AwardId.EaglePalmPin14Gold,
  AwardId.EaglePalmPin14Gold_2,
  AwardId.EaglePalmPin14Gold_3,
  AwardId.EaglePalmPin15Silver,
  AwardId.EaglePalmPin15Silver_2,
  AwardId.EaglePalmPin15Silver_3,
  AwardId.EaglePalmPin16Bronze,
  AwardId.EaglePalmPin16Bronze_2,
  AwardId.EaglePalmPin16Bronze_3,
  AwardId.EaglePalmPin17Gold,
  AwardId.EaglePalmPin17Gold_2,
  AwardId.EaglePalmPin17Gold_3,
  AwardId.EaglePalmPin18Silver,
  AwardId.EaglePalmPin18Silver_2,
  AwardId.EaglePalmPin18Silver_3,
  AwardId.EaglePalmPin19Bronze,
  AwardId.EaglePalmPin19Bronze_2,
  AwardId.EaglePalmPin19Bronze_3,
  AwardId.EaglePalmPin1Bronze,
  AwardId.EaglePalmPin1Bronze_2,
  AwardId.EaglePalmPin1Bronze_3,
  AwardId.EaglePalmPin20Gold,
  AwardId.EaglePalmPin20Gold_2,
  AwardId.EaglePalmPin20Gold_3,
  AwardId.EaglePalmPin21Silver,
  AwardId.EaglePalmPin21Silver_2,
  AwardId.EaglePalmPin21Silver_3,
  AwardId.EaglePalmPin22Bronze,
  AwardId.EaglePalmPin22Bronze_2,
  AwardId.EaglePalmPin22Bronze_3,
  AwardId.EaglePalmPin23Gold,
  AwardId.EaglePalmPin23Gold_2,
  AwardId.EaglePalmPin23Gold_3,
  AwardId.EaglePalmPin24Silver,
  AwardId.EaglePalmPin2Gold,
  AwardId.EaglePalmPin2Gold_2,
  AwardId.EaglePalmPin2Gold_3,
  AwardId.EaglePalmPin3Silver,
  AwardId.EaglePalmPin3Silver_2,
  AwardId.EaglePalmPin3Silver_3,
  AwardId.EaglePalmPin4Bronze,
  AwardId.EaglePalmPin4Bronze_2,
  AwardId.EaglePalmPin4Bronze_3,
  AwardId.EaglePalmPin5Gold,
  AwardId.EaglePalmPin5Gold_2,
  AwardId.EaglePalmPin5Gold_3,
  AwardId.EaglePalmPin6Silver,
  AwardId.EaglePalmPin6Silver_2,
  AwardId.EaglePalmPin6Silver_3,
  AwardId.EaglePalmPin7Bronze,
  AwardId.EaglePalmPin7Bronze_2,
  AwardId.EaglePalmPin7Bronze_3,
  AwardId.EaglePalmPin8Gold,
  AwardId.EaglePalmPin8Gold_2,
  AwardId.EaglePalmPin8Gold_3,
  AwardId.EaglePalmPin9Silver,
  AwardId.EaglePalmPin9Silver_2,
  AwardId.EaglePalmPin9Silver_3,
  AwardId.FiftyMilerAward,
  AwardId.FiremnChitScoutsBSA,
  AwardId.FoundersBarYouth,
  AwardId.GrandSlamOfNtnlHighAdvenAward,
  AwardId.HeroismAward,
  AwardId.HistoricTrailsAward,
  AwardId.HonorMedal,
  AwardId.HonorMedalWithCrossedPalms,
  AwardId.InternationalSpiritAwardYouth,
  AwardId.InterpreterStrip,
  AwardId.JamesEWestFellowshipAward,
  AwardId.JamesMStewartAward,
  AwardId.KayakingAward,
  AwardId.KayakingAward_2,
  AwardId.LocalCouncilCertificateOfMerit,
  AwardId.MedalOfMerit,
  AwardId.MessengerOfPeaceAmbassador,
  AwardId.MessengerOfPeaceChampion,
  AwardId.MessengersOfPeace,
  AwardId.MessengersOfPeaceDistinguishedServiceProject,
  AwardId.MessengersOfPeace_2,
  AwardId.MileSwimAward,
  AwardId.MileSwimAward_2,
  AwardId.NESALegacySocietyAwardYouth,
  AwardId.NationalHonorPatrol,
  AwardId.NationalMedalForOutdoorAchievement,
  AwardId.NatlCouncilCertOfMeritYouth,
  AwardId.NoaAwardAdventure,
  AwardId.NoaAwardAdventureDevice1Gold,
  AwardId.NoaAwardAdventureDevice2Gold,
  AwardId.NoaAwardAdventureDevice3Gold,
  AwardId.NoaAwardAdventureDevice4Silver,
  AwardId.NoaAwardAdventureDevice5Gold,
  AwardId.NoaAwardAdventureDevice6Gold,
  AwardId.NoaAwardAdventureDevice7Gold,
  AwardId.NoaAwardAdventureDevice8Silver,
  AwardId.NoaAwardAquatics,
  AwardId.NoaAwardAquaticsDevice1Gold,
  AwardId.NoaAwardAquaticsDevice2Gold,
  AwardId.NoaAwardAquaticsDevice3Gold,
  AwardId.NoaAwardAquaticsDevice4Silver,
  AwardId.NoaAwardAquaticsDevice5Gold,
  AwardId.NoaAwardAquaticsDevice6Gold,
  AwardId.NoaAwardAquaticsDevice7Gold,
  AwardId.NoaAwardAquaticsDevice8Silver,
  AwardId.NoaAwardCamping,
  AwardId.NoaAwardCampingDevice10Gold,
  AwardId.NoaAwardCampingDevice11Gold,
  AwardId.NoaAwardCampingDevice12Silver,
  AwardId.NoaAwardCampingDevice13Gold,
  AwardId.NoaAwardCampingDevice14Gold,
  AwardId.NoaAwardCampingDevice15Gold,
  AwardId.NoaAwardCampingDevice16Silver,
  AwardId.NoaAwardCampingDevice1Gold,
  AwardId.NoaAwardCampingDevice2Gold,
  AwardId.NoaAwardCampingDevice3Gold,
  AwardId.NoaAwardCampingDevice4Silver,
  AwardId.NoaAwardCampingDevice5Gold,
  AwardId.NoaAwardCampingDevice6Gold,
  AwardId.NoaAwardCampingDevice7Gold,
  AwardId.NoaAwardCampingDevice8Silver,
  AwardId.NoaAwardCampingDevice9Gold,
  AwardId.NoaAwardCenterEmblem,
  AwardId.NoaAwardConservation,
  AwardId.NoaAwardConservationDevice10Gold,
  AwardId.NoaAwardConservationDevice11Gold,
  AwardId.NoaAwardConservationDevice12Silver,
  AwardId.NoaAwardConservationDevice13Gold,
  AwardId.NoaAwardConservationDevice14Gold,
  AwardId.NoaAwardConservationDevice15Gold,
  AwardId.NoaAwardConservationDevice16Silver,
  AwardId.NoaAwardConservationDevice17Gold,
  AwardId.NoaAwardConservationDevice18Gold,
  AwardId.NoaAwardConservationDevice19Gold,
  AwardId.NoaAwardConservationDevice1Gold,
  AwardId.NoaAwardConservationDevice20Silver,
  AwardId.NoaAwardConservationDevice2Gold,
  AwardId.NoaAwardConservationDevice3Gold,
  AwardId.NoaAwardConservationDevice4Silver,
  AwardId.NoaAwardConservationDevice5Gold,
  AwardId.NoaAwardConservationDevice6Gold,
  AwardId.NoaAwardConservationDevice7Gold,
  AwardId.NoaAwardConservationDevice8Silver,
  AwardId.NoaAwardConservationDevice9Gold,
  AwardId.NoaAwardHiking,
  AwardId.NoaAwardHikingDevice10Gold,
  AwardId.NoaAwardHikingDevice11Gold,
  AwardId.NoaAwardHikingDevice12Silver,
  AwardId.NoaAwardHikingDevice13Gold,
  AwardId.NoaAwardHikingDevice14Gold,
  AwardId.NoaAwardHikingDevice15Gold,
  AwardId.NoaAwardHikingDevice16Silver,
  AwardId.NoaAwardHikingDevice17Gold,
  AwardId.NoaAwardHikingDevice18Gold,
  AwardId.NoaAwardHikingDevice19Gold,
  AwardId.NoaAwardHikingDevice1Gold,
  AwardId.NoaAwardHikingDevice20Silver,
  AwardId.NoaAwardHikingDevice2Gold,
  AwardId.NoaAwardHikingDevice3Gold,
  AwardId.NoaAwardHikingDevice4Silver,
  AwardId.NoaAwardHikingDevice5Gold,
  AwardId.NoaAwardHikingDevice6Gold,
  AwardId.NoaAwardHikingDevice7Gold,
  AwardId.NoaAwardHikingDevice8Silver,
  AwardId.NoaAwardHikingDevice9Gold,
  AwardId.NoaAwardRiding,
  AwardId.NoaAwardRidingDevice10Gold,
  AwardId.NoaAwardRidingDevice11Gold,
  AwardId.NoaAwardRidingDevice12Silver,
  AwardId.NoaAwardRidingDevice1Gold,
  AwardId.NoaAwardRidingDevice2Gold,
  AwardId.NoaAwardRidingDevice3Gold,
  AwardId.NoaAwardRidingDevice4Silver,
  AwardId.NoaAwardRidingDevice5Gold,
  AwardId.NoaAwardRidingDevice6Gold,
  AwardId.NoaAwardRidingDevice7Gold,
  AwardId.NoaAwardRidingDevice8Silver,
  AwardId.NoaAwardRidingDevice9Gold,
  AwardId.OrderOfTheArrowDistinguishedServiceAward,
  AwardId.OutdoorEthicsActionAwardScoutsBSA,
  AwardId.OutdoorEthicsAwarenessAwardScoutsBSA,
  AwardId.PaulBunyanCurrentScoutsBSA,
  AwardId.PaulBunyanWoodsman,
  AwardId.PolarBearAward,
  AwardId.RecruiterStrip,
  AwardId.ReligiousEmblemYouth,
  AwardId.ScoutingDistinguishedConservationServiceAwardScoutsBSA,
  AwardId.ScubaAward,
  AwardId.ScubaAward_2,
  AwardId.SnorkelingAward,
  AwardId.StandUpPaddleboardingAward,
  AwardId.TotinChip,
  AwardId.TotinChip2024,
  AwardId.TripleCrownNtnlHighAdvenAward,
  AwardId.WhitewaterRaftingAward,
  AwardId.WorldConservationAwardBoyScouts,
  AwardId.WorldConservationAwardV2016ScoutsBSA,
]);

export const SEA_SCOUT_AWARDS = new Set([
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNST,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNational,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearCouncil,
  AwardId.BoardsailingAward,
  AwardId.CommunityOrganizationAward,
  AwardId.CompleteAngler,
  AwardId.ConservationGoodTurnAwardSeaSct,
  AwardId.DenChiefServiceAward,
  AwardId.EaglePalmPin10Bronze,
  AwardId.EaglePalmPin10Bronze_2,
  AwardId.EaglePalmPin10Bronze_3,
  AwardId.EaglePalmPin11Gold,
  AwardId.EaglePalmPin11Gold_2,
  AwardId.EaglePalmPin11Gold_3,
  AwardId.EaglePalmPin12Silver,
  AwardId.EaglePalmPin12Silver_2,
  AwardId.EaglePalmPin12Silver_3,
  AwardId.EaglePalmPin13Bronze,
  AwardId.EaglePalmPin13Bronze_2,
  AwardId.EaglePalmPin13Bronze_3,
  AwardId.EaglePalmPin14Gold,
  AwardId.EaglePalmPin14Gold_2,
  AwardId.EaglePalmPin14Gold_3,
  AwardId.EaglePalmPin15Silver,
  AwardId.EaglePalmPin15Silver_2,
  AwardId.EaglePalmPin15Silver_3,
  AwardId.EaglePalmPin16Bronze,
  AwardId.EaglePalmPin16Bronze_2,
  AwardId.EaglePalmPin16Bronze_3,
  AwardId.EaglePalmPin17Gold,
  AwardId.EaglePalmPin17Gold_2,
  AwardId.EaglePalmPin17Gold_3,
  AwardId.EaglePalmPin18Silver,
  AwardId.EaglePalmPin18Silver_2,
  AwardId.EaglePalmPin18Silver_3,
  AwardId.EaglePalmPin19Bronze,
  AwardId.EaglePalmPin19Bronze_2,
  AwardId.EaglePalmPin19Bronze_3,
  AwardId.EaglePalmPin1Bronze,
  AwardId.EaglePalmPin1Bronze_2,
  AwardId.EaglePalmPin1Bronze_3,
  AwardId.EaglePalmPin20Gold,
  AwardId.EaglePalmPin20Gold_2,
  AwardId.EaglePalmPin20Gold_3,
  AwardId.EaglePalmPin21Silver,
  AwardId.EaglePalmPin21Silver_2,
  AwardId.EaglePalmPin21Silver_3,
  AwardId.EaglePalmPin22Bronze,
  AwardId.EaglePalmPin22Bronze_2,
  AwardId.EaglePalmPin22Bronze_3,
  AwardId.EaglePalmPin23Gold,
  AwardId.EaglePalmPin23Gold_2,
  AwardId.EaglePalmPin23Gold_3,
  AwardId.EaglePalmPin24Silver,
  AwardId.EaglePalmPin2Gold,
  AwardId.EaglePalmPin2Gold_2,
  AwardId.EaglePalmPin2Gold_3,
  AwardId.EaglePalmPin3Silver,
  AwardId.EaglePalmPin3Silver_2,
  AwardId.EaglePalmPin4Bronze,
  AwardId.EaglePalmPin4Bronze_2,
  AwardId.EaglePalmPin4Bronze_3,
  AwardId.EaglePalmPin5Gold,
  AwardId.EaglePalmPin5Gold_2,
  AwardId.EaglePalmPin5Gold_3,
  AwardId.EaglePalmPin6Silver,
  AwardId.EaglePalmPin6Silver_2,
  AwardId.EaglePalmPin6Silver_3,
  AwardId.EaglePalmPin7Bronze,
  AwardId.EaglePalmPin7Bronze_2,
  AwardId.EaglePalmPin7Bronze_3,
  AwardId.EaglePalmPin8Gold,
  AwardId.EaglePalmPin8Gold_2,
  AwardId.EaglePalmPin8Gold_3,
  AwardId.EaglePalmPin9Silver,
  AwardId.EaglePalmPin9Silver_2,
  AwardId.EaglePalmPin9Silver_3,
  AwardId.FiftyMilerAward,
  AwardId.FoundersBarYouth,
  AwardId.GrandSlamOfNtnlHighAdvenAward,
  AwardId.HeroismAward,
  AwardId.HistoricTrailsAward,
  AwardId.HonorMedal,
  AwardId.HonorMedalWithCrossedPalms,
  AwardId.InternationalSpiritAwardYouth,
  AwardId.InterpreterStrip,
  AwardId.JamesEWestFellowshipAward,
  AwardId.JamesMStewartAward,
  AwardId.KayakingAward,
  AwardId.KayakingAward_2,
  AwardId.LocalCouncilCertificateOfMerit,
  AwardId.LongCruise,
  AwardId.MedalOfMerit,
  AwardId.MessengerOfPeaceAmbassador,
  AwardId.MessengerOfPeaceChampion,
  AwardId.MessengersOfPeace,
  AwardId.MessengersOfPeaceDistinguishedServiceProject,
  AwardId.MessengersOfPeace_2,
  AwardId.MileSwimAward,
  AwardId.MileSwimAward_2,
  AwardId.NESALegacySocietyAwardYouth,
  AwardId.NationalMedalForOutdoorAchievement,
  AwardId.NatlCouncilCertOfMeritYouth,
  AwardId.NoaAwardAdventure,
  AwardId.NoaAwardAdventureDevice1Gold,
  AwardId.NoaAwardAdventureDevice2Gold,
  AwardId.NoaAwardAdventureDevice3Gold,
  AwardId.NoaAwardAdventureDevice4Silver,
  AwardId.NoaAwardAdventureDevice5Gold,
  AwardId.NoaAwardAdventureDevice6Gold,
  AwardId.NoaAwardAdventureDevice7Gold,
  AwardId.NoaAwardAdventureDevice8Silver,
  AwardId.NoaAwardAquatics,
  AwardId.NoaAwardAquaticsDevice1Gold,
  AwardId.NoaAwardAquaticsDevice2Gold,
  AwardId.NoaAwardAquaticsDevice3Gold,
  AwardId.NoaAwardAquaticsDevice4Silver,
  AwardId.NoaAwardAquaticsDevice5Gold,
  AwardId.NoaAwardAquaticsDevice6Gold,
  AwardId.NoaAwardAquaticsDevice7Gold,
  AwardId.NoaAwardAquaticsDevice8Silver,
  AwardId.NoaAwardCamping,
  AwardId.NoaAwardCampingDevice10Gold,
  AwardId.NoaAwardCampingDevice11Gold,
  AwardId.NoaAwardCampingDevice12Silver,
  AwardId.NoaAwardCampingDevice13Gold,
  AwardId.NoaAwardCampingDevice14Gold,
  AwardId.NoaAwardCampingDevice15Gold,
  AwardId.NoaAwardCampingDevice16Silver,
  AwardId.NoaAwardCampingDevice1Gold,
  AwardId.NoaAwardCampingDevice2Gold,
  AwardId.NoaAwardCampingDevice3Gold,
  AwardId.NoaAwardCampingDevice4Silver,
  AwardId.NoaAwardCampingDevice5Gold,
  AwardId.NoaAwardCampingDevice6Gold,
  AwardId.NoaAwardCampingDevice7Gold,
  AwardId.NoaAwardCampingDevice8Silver,
  AwardId.NoaAwardCampingDevice9Gold,
  AwardId.NoaAwardCenterEmblem,
  AwardId.NoaAwardConservation,
  AwardId.NoaAwardConservationDevice10Gold,
  AwardId.NoaAwardConservationDevice11Gold,
  AwardId.NoaAwardConservationDevice12Silver,
  AwardId.NoaAwardConservationDevice13Gold,
  AwardId.NoaAwardConservationDevice14Gold,
  AwardId.NoaAwardConservationDevice15Gold,
  AwardId.NoaAwardConservationDevice16Silver,
  AwardId.NoaAwardConservationDevice17Gold,
  AwardId.NoaAwardConservationDevice18Gold,
  AwardId.NoaAwardConservationDevice19Gold,
  AwardId.NoaAwardConservationDevice1Gold,
  AwardId.NoaAwardConservationDevice20Silver,
  AwardId.NoaAwardConservationDevice2Gold,
  AwardId.NoaAwardConservationDevice3Gold,
  AwardId.NoaAwardConservationDevice4Silver,
  AwardId.NoaAwardConservationDevice5Gold,
  AwardId.NoaAwardConservationDevice6Gold,
  AwardId.NoaAwardConservationDevice7Gold,
  AwardId.NoaAwardConservationDevice8Silver,
  AwardId.NoaAwardConservationDevice9Gold,
  AwardId.NoaAwardHiking,
  AwardId.NoaAwardHikingDevice10Gold,
  AwardId.NoaAwardHikingDevice11Gold,
  AwardId.NoaAwardHikingDevice12Silver,
  AwardId.NoaAwardHikingDevice13Gold,
  AwardId.NoaAwardHikingDevice14Gold,
  AwardId.NoaAwardHikingDevice15Gold,
  AwardId.NoaAwardHikingDevice16Silver,
  AwardId.NoaAwardHikingDevice17Gold,
  AwardId.NoaAwardHikingDevice18Gold,
  AwardId.NoaAwardHikingDevice19Gold,
  AwardId.NoaAwardHikingDevice1Gold,
  AwardId.NoaAwardHikingDevice20Silver,
  AwardId.NoaAwardHikingDevice2Gold,
  AwardId.NoaAwardHikingDevice3Gold,
  AwardId.NoaAwardHikingDevice4Silver,
  AwardId.NoaAwardHikingDevice5Gold,
  AwardId.NoaAwardHikingDevice6Gold,
  AwardId.NoaAwardHikingDevice7Gold,
  AwardId.NoaAwardHikingDevice8Silver,
  AwardId.NoaAwardHikingDevice9Gold,
  AwardId.NoaAwardRiding,
  AwardId.NoaAwardRidingDevice10Gold,
  AwardId.NoaAwardRidingDevice11Gold,
  AwardId.NoaAwardRidingDevice12Silver,
  AwardId.NoaAwardRidingDevice1Gold,
  AwardId.NoaAwardRidingDevice2Gold,
  AwardId.NoaAwardRidingDevice3Gold,
  AwardId.NoaAwardRidingDevice4Silver,
  AwardId.NoaAwardRidingDevice5Gold,
  AwardId.NoaAwardRidingDevice6Gold,
  AwardId.NoaAwardRidingDevice7Gold,
  AwardId.NoaAwardRidingDevice8Silver,
  AwardId.NoaAwardRidingDevice9Gold,
  AwardId.OrderOfTheArrowDistinguishedServiceAward,
  AwardId.PolarBearAward,
  AwardId.QualifiedSeamanAward,
  AwardId.RecruiterStrip,
  AwardId.ReligiousEmblemYouth,
  AwardId.ScoutingDistinguishedConservationServiceAwardSeaScouting,
  AwardId.ScubaAward,
  AwardId.ScubaAward_2,
  AwardId.SeaScoutLeadershipAwardCouncil,
  AwardId.SeaScoutLeadershipAwardNational,
  AwardId.SmallBoatHandler,
  AwardId.SnorkelingAward,
  AwardId.StandUpPaddleboardingAward,
  AwardId.TripleCrownNtnlHighAdvenAward,
  AwardId.USPSFinleySeaScoutServiceAward,
  AwardId.WhitewaterRaftingAward,
]);

export const VENTURING_AWARDS = new Set([
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNST,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearAwardNational,
  AwardId.AdamsEagleScoutServiceProjectOfTheYearCouncil,
  AwardId.BoardsailingAward,
  AwardId.CommunityOrganizationAward,
  AwardId.CompleteAngler,
  AwardId.ConservationGoodTurnAwardVen,
  AwardId.DenChiefServiceAward,
  AwardId.EaglePalmPin10Bronze,
  AwardId.EaglePalmPin10Bronze_2,
  AwardId.EaglePalmPin10Bronze_3,
  AwardId.EaglePalmPin11Gold,
  AwardId.EaglePalmPin11Gold_2,
  AwardId.EaglePalmPin11Gold_3,
  AwardId.EaglePalmPin12Silver,
  AwardId.EaglePalmPin12Silver_2,
  AwardId.EaglePalmPin12Silver_3,
  AwardId.EaglePalmPin13Bronze,
  AwardId.EaglePalmPin13Bronze_2,
  AwardId.EaglePalmPin13Bronze_3,
  AwardId.EaglePalmPin14Gold,
  AwardId.EaglePalmPin14Gold_2,
  AwardId.EaglePalmPin14Gold_3,
  AwardId.EaglePalmPin15Silver,
  AwardId.EaglePalmPin15Silver_2,
  AwardId.EaglePalmPin15Silver_3,
  AwardId.EaglePalmPin16Bronze,
  AwardId.EaglePalmPin16Bronze_2,
  AwardId.EaglePalmPin16Bronze_3,
  AwardId.EaglePalmPin17Gold,
  AwardId.EaglePalmPin17Gold_2,
  AwardId.EaglePalmPin17Gold_3,
  AwardId.EaglePalmPin18Silver,
  AwardId.EaglePalmPin18Silver_2,
  AwardId.EaglePalmPin18Silver_3,
  AwardId.EaglePalmPin19Bronze,
  AwardId.EaglePalmPin19Bronze_2,
  AwardId.EaglePalmPin19Bronze_3,
  AwardId.EaglePalmPin1Bronze,
  AwardId.EaglePalmPin1Bronze_2,
  AwardId.EaglePalmPin1Bronze_3,
  AwardId.EaglePalmPin20Gold,
  AwardId.EaglePalmPin20Gold_2,
  AwardId.EaglePalmPin20Gold_3,
  AwardId.EaglePalmPin21Silver,
  AwardId.EaglePalmPin21Silver_2,
  AwardId.EaglePalmPin21Silver_3,
  AwardId.EaglePalmPin22Bronze,
  AwardId.EaglePalmPin22Bronze_2,
  AwardId.EaglePalmPin22Bronze_3,
  AwardId.EaglePalmPin23Gold,
  AwardId.EaglePalmPin23Gold_2,
  AwardId.EaglePalmPin23Gold_3,
  AwardId.EaglePalmPin24Silver,
  AwardId.EaglePalmPin2Gold,
  AwardId.EaglePalmPin2Gold_2,
  AwardId.EaglePalmPin2Gold_3,
  AwardId.EaglePalmPin3Silver,
  AwardId.EaglePalmPin3Silver_2,
  AwardId.EaglePalmPin4Bronze,
  AwardId.EaglePalmPin4Bronze_2,
  AwardId.EaglePalmPin4Bronze_3,
  AwardId.EaglePalmPin5Gold,
  AwardId.EaglePalmPin5Gold_2,
  AwardId.EaglePalmPin5Gold_3,
  AwardId.EaglePalmPin6Silver,
  AwardId.EaglePalmPin6Silver_2,
  AwardId.EaglePalmPin6Silver_3,
  AwardId.EaglePalmPin7Bronze,
  AwardId.EaglePalmPin7Bronze_2,
  AwardId.EaglePalmPin7Bronze_3,
  AwardId.EaglePalmPin8Gold,
  AwardId.EaglePalmPin8Gold_2,
  AwardId.EaglePalmPin8Gold_3,
  AwardId.EaglePalmPin9Silver,
  AwardId.EaglePalmPin9Silver_2,
  AwardId.EaglePalmPin9Silver_3,
  AwardId.FiftyMilerAward,
  AwardId.FoundersBarYouth,
  AwardId.GrandSlamOfNtnlHighAdvenAward,
  AwardId.HeroismAward,
  AwardId.HistoricTrailsAward,
  AwardId.HonorMedal,
  AwardId.HonorMedalWithCrossedPalms,
  AwardId.InternationalSpiritAwardYouth,
  AwardId.InterpreterStrip,
  AwardId.JamesEWestFellowshipAward,
  AwardId.JamesMStewartAward,
  AwardId.KayakingAward,
  AwardId.KayakingAward_2,
  AwardId.LocalCouncilCertificateOfMerit,
  AwardId.MedalOfMerit,
  AwardId.MessengerOfPeaceAmbassador,
  AwardId.MessengerOfPeaceChampion,
  AwardId.MessengersOfPeace,
  AwardId.MessengersOfPeaceDistinguishedServiceProject,
  AwardId.MessengersOfPeace_2,
  AwardId.MileSwimAward,
  AwardId.MileSwimAward_2,
  AwardId.NESALegacySocietyAwardYouth,
  AwardId.NationalMedalForOutdoorAchievement,
  AwardId.NatlCouncilCertOfMeritYouth,
  AwardId.NoaAwardAdventure,
  AwardId.NoaAwardAdventureDevice1Gold,
  AwardId.NoaAwardAdventureDevice2Gold,
  AwardId.NoaAwardAdventureDevice3Gold,
  AwardId.NoaAwardAdventureDevice4Silver,
  AwardId.NoaAwardAdventureDevice5Gold,
  AwardId.NoaAwardAdventureDevice6Gold,
  AwardId.NoaAwardAdventureDevice7Gold,
  AwardId.NoaAwardAdventureDevice8Silver,
  AwardId.NoaAwardAquatics,
  AwardId.NoaAwardAquaticsDevice1Gold,
  AwardId.NoaAwardAquaticsDevice2Gold,
  AwardId.NoaAwardAquaticsDevice3Gold,
  AwardId.NoaAwardAquaticsDevice4Silver,
  AwardId.NoaAwardAquaticsDevice5Gold,
  AwardId.NoaAwardAquaticsDevice6Gold,
  AwardId.NoaAwardAquaticsDevice7Gold,
  AwardId.NoaAwardAquaticsDevice8Silver,
  AwardId.NoaAwardCamping,
  AwardId.NoaAwardCampingDevice10Gold,
  AwardId.NoaAwardCampingDevice11Gold,
  AwardId.NoaAwardCampingDevice12Silver,
  AwardId.NoaAwardCampingDevice13Gold,
  AwardId.NoaAwardCampingDevice14Gold,
  AwardId.NoaAwardCampingDevice15Gold,
  AwardId.NoaAwardCampingDevice16Silver,
  AwardId.NoaAwardCampingDevice1Gold,
  AwardId.NoaAwardCampingDevice2Gold,
  AwardId.NoaAwardCampingDevice3Gold,
  AwardId.NoaAwardCampingDevice4Silver,
  AwardId.NoaAwardCampingDevice5Gold,
  AwardId.NoaAwardCampingDevice6Gold,
  AwardId.NoaAwardCampingDevice7Gold,
  AwardId.NoaAwardCampingDevice8Silver,
  AwardId.NoaAwardCampingDevice9Gold,
  AwardId.NoaAwardCenterEmblem,
  AwardId.NoaAwardConservation,
  AwardId.NoaAwardConservationDevice10Gold,
  AwardId.NoaAwardConservationDevice11Gold,
  AwardId.NoaAwardConservationDevice12Silver,
  AwardId.NoaAwardConservationDevice13Gold,
  AwardId.NoaAwardConservationDevice14Gold,
  AwardId.NoaAwardConservationDevice15Gold,
  AwardId.NoaAwardConservationDevice16Silver,
  AwardId.NoaAwardConservationDevice17Gold,
  AwardId.NoaAwardConservationDevice18Gold,
  AwardId.NoaAwardConservationDevice19Gold,
  AwardId.NoaAwardConservationDevice1Gold,
  AwardId.NoaAwardConservationDevice20Silver,
  AwardId.NoaAwardConservationDevice2Gold,
  AwardId.NoaAwardConservationDevice3Gold,
  AwardId.NoaAwardConservationDevice4Silver,
  AwardId.NoaAwardConservationDevice5Gold,
  AwardId.NoaAwardConservationDevice6Gold,
  AwardId.NoaAwardConservationDevice7Gold,
  AwardId.NoaAwardConservationDevice8Silver,
  AwardId.NoaAwardConservationDevice9Gold,
  AwardId.NoaAwardHiking,
  AwardId.NoaAwardHikingDevice10Gold,
  AwardId.NoaAwardHikingDevice11Gold,
  AwardId.NoaAwardHikingDevice12Silver,
  AwardId.NoaAwardHikingDevice13Gold,
  AwardId.NoaAwardHikingDevice14Gold,
  AwardId.NoaAwardHikingDevice15Gold,
  AwardId.NoaAwardHikingDevice16Silver,
  AwardId.NoaAwardHikingDevice17Gold,
  AwardId.NoaAwardHikingDevice18Gold,
  AwardId.NoaAwardHikingDevice19Gold,
  AwardId.NoaAwardHikingDevice1Gold,
  AwardId.NoaAwardHikingDevice20Silver,
  AwardId.NoaAwardHikingDevice2Gold,
  AwardId.NoaAwardHikingDevice3Gold,
  AwardId.NoaAwardHikingDevice4Silver,
  AwardId.NoaAwardHikingDevice5Gold,
  AwardId.NoaAwardHikingDevice6Gold,
  AwardId.NoaAwardHikingDevice7Gold,
  AwardId.NoaAwardHikingDevice8Silver,
  AwardId.NoaAwardHikingDevice9Gold,
  AwardId.NoaAwardRiding,
  AwardId.NoaAwardRidingDevice10Gold,
  AwardId.NoaAwardRidingDevice11Gold,
  AwardId.NoaAwardRidingDevice12Silver,
  AwardId.NoaAwardRidingDevice1Gold,
  AwardId.NoaAwardRidingDevice2Gold,
  AwardId.NoaAwardRidingDevice3Gold,
  AwardId.NoaAwardRidingDevice4Silver,
  AwardId.NoaAwardRidingDevice5Gold,
  AwardId.NoaAwardRidingDevice6Gold,
  AwardId.NoaAwardRidingDevice7Gold,
  AwardId.NoaAwardRidingDevice8Silver,
  AwardId.NoaAwardRidingDevice9Gold,
  AwardId.OrderOfTheArrowDistinguishedServiceAward,
  AwardId.OutdoorEthicsActionAwardVenturerSeaScout,
  AwardId.OutdoorEthicsAwarenessAwardVenturerSeaScout,
  AwardId.PolarBearAward,
  AwardId.RecruiterStrip,
  AwardId.ReligiousEmblemYouth,
  AwardId.ScoutingDistinguishedConservationServiceAwardVenturing,
  AwardId.ScubaAward,
  AwardId.ScubaAward_2,
  AwardId.SnorkelingAward,
  AwardId.StandUpPaddleboardingAward,
  AwardId.TripleCrownNtnlHighAdvenAward,
  AwardId.VenturingLeadershipAwardYouthCncl,
  AwardId.VenturingLeadershipAwardYouthNST,
  AwardId.VenturingLeadershipAwardYouthNatl,
  AwardId.VenturingQuestAward,
  AwardId.VenturingRangerAward,
  IS_QA_ENV ? AwardId.VenturingRangerAward2025 : 0,
  AwardId.VenturingShootingSportsOutstandingAchievementAward,
  AwardId.VenturingTRUSTAward,
  AwardId.VenturingWorldConservationAward,
  AwardId.WhitewaterRaftingAward,
]);

export const EXPLORING_AWARDS = new Set([
  AwardId.ExploringBraveryAward,
  AwardId.ExploringCareerAchievementAwardArtsHumanities,
  AwardId.ExploringCareerAchievementAwardAviation,
  AwardId.ExploringCareerAchievementAwardBusiness,
  AwardId.ExploringCareerAchievementAwardCommunications,
  AwardId.ExploringCareerAchievementAwardEngineeringTechnology,
  AwardId.ExploringCareerAchievementAwardFireEMS,
  AwardId.ExploringCareerAchievementAwardHealthCare,
  AwardId.ExploringCareerAchievementAwardLawEnforcement,
  AwardId.ExploringCareerAchievementAwardLawGovernment,
  AwardId.ExploringCareerAchievementAwardScience,
  AwardId.ExploringCareerAchievementAwardSkilledTrades,
  AwardId.ExploringCareerAchievementAwardSocialServices,
  AwardId.ExploringCareerAwarenessAward,
  AwardId.ExploringCongressionalAwardBronzeCertificate,
  AwardId.ExploringCongressionalAwardBronzeMedal,
  AwardId.ExploringCongressionalAwardGoldCertificate,
  AwardId.ExploringCongressionalAwardGoldMedal,
  AwardId.ExploringCongressionalAwardSilverCertificate,
  AwardId.ExploringCongressionalAwardSilverMedal,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardCommunityService,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardConferenceBar,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardDrugAbusePrevention,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardEagleGoldRecognition,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardEmergencyPreparedness,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardExplorerOfTheYear,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardPerfectAttendance,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardPrevention,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardService,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardTenure,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardTraining,
  AwardId.ExploringLawEnforcementAndFireEmergencyServicesAwardWilliamHSpurgeonIII,
  AwardId.ExploringLawEnforcementAwardFirearmsTraining,
  AwardId.ExploringLawEnforcementAwardLeadershipAcademy,
  AwardId.ExploringLawEnforcementAwardPhysicalFitness,
  AwardId.ExploringLearningForLifeLeadershipAward,
  AwardId.ExploringLearningForLifeLifesavingAward,
  AwardId.ExploringLearningForLifeMeritoriousActionAward,
  AwardId.ExploringLearningForLifePresidentSLifetimeAchievementAward,
  AwardId.ExploringLearningForLifePresidentSVolunteerServiceAwardBronze,
  AwardId.ExploringLearningForLifePresidentSVolunteerServiceAwardGold,
  AwardId.ExploringLearningForLifePresidentSVolunteerServiceAwardSilver,
  AwardId.ExploringLifesavingAward,
  AwardId.ExploringMedalOfValorAward,
  AwardId.ExploringMeritoriousServiceAward,
  AwardId.HeroismAward,
  AwardId.HonorMedal,
  AwardId.HonorMedalWithCrossedPalms,
  AwardId.JamesEWestFellowshipAward,
  AwardId.LocalCouncilCertificateOfMerit,
  AwardId.MedalOfMerit,
  AwardId.MessengersOfPeace,
  AwardId.NatlCouncilCertOfMeritYouth,
]);

export const awardsByProgram: Record<Program, Set<AwardId>> = Object.freeze({
  [Program.CUB_SCOUT]: CUB_SCOUT_AWARDS,
  [Program.BOY_SCOUT]: SCOUTS_BSA_AWARDS,
  [Program.SEA_SCOUT]: SEA_SCOUT_AWARDS,
  [Program.VENTURING]: VENTURING_AWARDS,
  [Program.EXPLORING]: EXPLORING_AWARDS,
  [Program.VARSITY]: new Set([]),
  [Program.CLUB]: new Set([]),
});

export const subUnitGroupingAllowed = [
  ProgramId.BOY_SCOUT,
  ProgramId.CUB_SCOUT,
];

export const advancementCommentRequest = {};
export const advancementCommentSubject = 'subject-post';

export const toPurchaseStatusesArray = [
  PendingItemStatus.LEADER_APPROVED,
  PendingItemStatus.AWARDED,
];

export const toPurchaseStatusesString = _.chain(toPurchaseStatusesArray)
  .uniq()
  .sort()
  .join(',')
  .value();
